import { createContext, useContext, useMemo } from 'react';
import { useGetWebSettings } from '../../APICalling/useApiQueries';
import BackgroundImg from '../../Assets/Images/home_bg.jpg';
import LogoImg from '../../Assets/Images/logo.svg';
import { resolveMaxServiceCount } from '../../Utils/bookingConfig';

const WebSettingsContext = createContext(null);

/**
 * Fetches /web-settings once (via React Query) and exposes them app-wide.
 * Use: const { title, headerLogoUrl, deposit, ... } = useWebSettings();
 */
export const WebSettingsProvider = ({ children }) => {
  const { data, isLoading, isPending, isFetching, isPaused, failureCount, isError, error, refetch } = useGetWebSettings({}, {
    // Poll only after settings have loaded; initial failures use bounded retries.
    refetchInterval: (query) => query.state.data?.data?.data ? 30000 : false,
    refetchOnWindowFocus: true,
    staleTime: 30000,
    // Keep settings failures available to the application status gate.
    throwOnError: false,
  });
  const raw = data?.data?.data;

  const value = useMemo(() => {
    const toNumber = (val, fallback = null) => {
      if (val === undefined || val === null || val === '') return fallback;
      const num = Number(val);
      return Number.isNaN(num) ? fallback : num;
    };

    return {
      headerLogoUrl: raw?.header_logo_url || LogoImg,
      backgroundImageUrl: raw?.background_image_url || BackgroundImg,
      // headerLogoUrl: LogoImg,
      // backgroundImageUrl: BackgroundImg,
      title: raw?.title || "Pure. Natural. Simple.",
      subTitle: raw?.sub_title || "Experience True Massage and Therapy",
      description: raw?.description,
      timerValue: toNumber(raw?.set_timer_value) || 10,
      deposit: toNumber(raw?.deposit) || 20,
      maxServiceCount: resolveMaxServiceCount(raw?.limit_value) || 3,
      rescheduleMaxLimit: toNumber(raw?.reschedule_max_limit),
      rescheduleTimeLimit: toNumber(raw?.reschedule_time_limit),
      maintenanceMode: raw?.maintenance_mode === true || raw?.maintenance_mode === 1 || raw?.maintenance_mode === 'true' || raw?.maintenance_mode === '1',
      maintenanceMessage: typeof raw?.maintenance_message === 'string' ? raw.maintenance_message.trim() : '',
      maintenanceEndTime: typeof raw?.maintenance_end_time === 'string' ? raw.maintenance_end_time.trim() : '',
      raw: raw || {},
      isLoading,
      isPending,
      isFetching,
      isPaused,
      failureCount,
      hasSettings: Boolean(raw),
      isError,
      error,
      refetch,
    };
  }, [raw, isLoading, isPending, isFetching, isPaused, failureCount, isError, error, refetch]);

  return (
    <WebSettingsContext.Provider value={value}>
      {children}
    </WebSettingsContext.Provider>
  );
};

export const useWebSettings = () => {
  const context = useContext(WebSettingsContext);
  if (!context) {
    throw new Error('useWebSettings must be used within WebSettingsProvider');
  }
  return context;
};

export default WebSettingsContext;
